import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {SkippableBenchlingQueryResult, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

import {
  ProcessFlowchartConfigEditorQuery,
  ProcessFlowchartConfigEditorQueryVariables,
} from './__generated/ProcessFlowchartConfigEditorQuery';

export const PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY: DocumentNode = gql`
  query ProcessFlowchartConfigEditorQuery(
    $flowchartConfigId: StrictString!
    $flowchartConfigVersionId: StrictString
  ) @skipAudit @engComponent(name: WORKFLOWS) {
    processFlowchartConfig(id: $flowchartConfigId) {
      ... on ProcessFlowchartConfigNode {
        ...ProcessFlowchartConfigEditorGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfigEditor').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Hook to load a process flowchart config from the server.
 *
 * @param flowchartConfigId - The ID of the flowchart config to load, or null to skip.
 * @param flowchartConfigVersionId - Optional version ID override. If provided, loads this
 *   specific version instead of the default (current) version.
 * @param options - Optional configuration. Set `networkOnly: true` to bypass the Apollo cache.
 */
export function useProcessFlowchartConfigQuery(
  flowchartConfigId: string | null,
  flowchartConfigVersionId?: string | null,
  options?: {networkOnly?: boolean}
): SkippableBenchlingQueryResult<
  ProcessFlowchartConfigEditorQuery,
  ProcessFlowchartConfigEditorQueryVariables
> {
  return useSkippableBenchlingQuery<
    ProcessFlowchartConfigEditorQuery,
    ProcessFlowchartConfigEditorQueryVariables
  >(PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY, {
    variables: {
      flowchartConfigId: flowchartConfigId ?? '',
      flowchartConfigVersionId: flowchartConfigVersionId ?? undefined,
    },
    skip: flowchartConfigId == null,
    ...(options?.networkOnly && {fetchPolicy: 'network-only' as const}),
  });
}
